import {Tag} from '@/components-v2/atoms/Tag/Tag'
import {Text} from '@/components-v2/atoms/Text/Text'
import {Toggle} from '@/components-v2/atoms/Toggle/Toggle'
import {TextArea} from '@/components-v2/molecules/TextArea/TextArea'
import {TextInput} from '@/components-v2/molecules/TextInput/TextInput'
import {DatePicker} from '@/components-v2/molecules/DatePicker/DatePicker'
import {TAG_COLOR} from '@/utils/constants/colors'
import {useProductReview} from '../../context/ProductReviewContext'
import {getDisplayValue} from './productReviewUtils'
import {SegmentDropdown} from './Segment/SegmentDropdown'
import {splitISOToLocalDate} from '@/helpers/dateUtils'


/**
 * @typedef {Object} ProductReviewFieldProps
 * @property {string} label Field label.
 * @property {boolean} editable Whether the field can be edited.
 * @property {boolean} required Whether the field is required (includes conditional requirements).
 * @property {import('../ProductReviewTabs/productReviewDataMap').ProductReviewRenderer | string} renderer Field renderer type.
 * @property {unknown} value Field value.
 * @property {string} [labelKey] Optional key for label identification.
 * @property {(value: unknown, onChange: (value: unknown) => void) => import('react').ReactNode} [customRenderer] Optional custom render function that overrides the default edit control for this field.
 * @property {string} [error] Optional error text shown under the editable control.
 * @property {(value: unknown) => void} [onChange] Callback triggered when editable field value changes.
 */

/**
 * Displays a product review field as read-only text or editable form control.
 * @param {ProductReviewFieldProps} props
 */
export const ProductReviewField = ({
  label,
  editable,
  required,
  renderer,
  value,
  labelKey,
  customRenderer,
  error,
  onChange = () => {},
  dependentDate,
}) => {
  const {isEditMode} = useProductReview()
  const displayValue = getDisplayValue(value, renderer)
  const isFieldEditable = Boolean(isEditMode && editable)
  const isToggleEnabled = value === true || value === 'true'
  const textAreaValue = typeof value === 'string' ? value : ''
  const textInputValue = typeof value === 'string' || typeof value === 'number' ? String(value) : ''
   const dependentDateValue = typeof dependentDate === 'string' ? splitISOToLocalDate(dependentDate) : ''

  const renderEditControl = () => {
    if (customRenderer) {
      return customRenderer(value, onChange)
    }

    if (labelKey === 'segmentDetails') {
      return <SegmentDropdown label={label} value={value ?? []} onChange={onChange} />
    }

    if (labelKey === 'skuStartDate') {
      return (
         <DatePicker 
          label={label}
          value={displayValue} 
          onChange={onChange} 
          menuPosition="bottom-left"
          validationRules={[
            {type: 'startDate', endDate: dependentDateValue},
          ]}
        />
      )
    }

     if (labelKey === 'skuEndDate') {
      return (
         <DatePicker 
          label={label}
          value={displayValue} 
          onChange={onChange} 
          menuPosition="bottom-left"
          validationRules={[
            {type: 'endDate', startDate: dependentDateValue},
          ]}
        />
      )
    }

    if (renderer === 'toggle') {
      return <Toggle checked={isToggleEnabled} onUpdate={checked => onChange(checked)} size="small" />
    }

    if (renderer === 'textarea') {
      return (
        <TextArea
          value={textAreaValue}
          onUpdate={nextValue => onChange(nextValue)}
          className="w-full"
          showToolbar={false}
          showCharacterCount={false}
          style={{minWidth: 'auto'}}
          error={error}
        />
      )
    }

    return (
      <TextInput value={textInputValue} onUpdate={nextValue => onChange(nextValue)} className="w-full" error={error} />
    )
  }

  return (
    <div className="flex items-start py-2">
      <div className="w-1/2 pr-4">
        <Text size="small" data-cy={labelKey ? `${labelKey}-label` : undefined}>
          {`${label}${isEditMode && required ? ' *' : ''}`}
        </Text>
      </div>
      <div className="w-1/2">
        {isFieldEditable ? (
          renderEditControl()
        ) : renderer === 'toggle' ? (
          <Tag text={displayValue} color={isToggleEnabled ? TAG_COLOR.GREEN : TAG_COLOR.RED} />
        ) : (
          <Text variant="medium">{displayValue}</Text>
        )}
      </div>
    </div>
  )
}
